/**
 * "Ocultar": the caregiver hides symbols or categories the child is not ready
 * for yet, and shows them again later. Nothing is deleted.
 */
import type { Hidden } from '@shared/schemas';

/** Returns `list` with `id` added (`hide`) or removed. Same array when nothing changes. */
export function setIn(list: string[], id: string, hide: boolean): string[] {
  const has = list.includes(id);
  if (hide === has) return list;
  return hide ? [...list, id] : list.filter((x) => x !== id);
}

/** Hides (or shows) several symbols at once, e.g. every symbol of a category. */
export function setManyIn(list: string[], ids: string[], hide: boolean): string[] {
  if (hide) return [...new Set([...list, ...ids])];
  const remove = new Set(ids);
  return list.filter((x) => !remove.has(x));
}

export function hiddenSets(hidden: Hidden) {
  return { symbols: new Set(hidden.symbols), categories: new Set(hidden.categories) };
}

/**
 * What the child sees in a category: hidden symbols leave an empty space
 * (`null`), so the visible ones keep their place when more are shown later
 * (motor planning). Empty spaces after the last visible symbol are dropped.
 */
export function withGaps<T extends { id: string }>(symbols: T[], hidden: Set<string>): (T | null)[] {
  const cells = symbols.map((s) => (hidden.has(s.id) ? null : s));
  while (cells.length && cells[cells.length - 1] === null) cells.pop();
  return cells;
}
